import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Divider,
  IconButton,
  Chip,
  Stack,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import ScheduleIcon from "@mui/icons-material/Schedule";

import { ParseLogJson } from "src/shared/Utils/FormatLogs/ParseLogJson";
import { FormatLogValue } from "src/shared/Utils/FormatLogs/FormatLogValue";
import { FormatLogLabel } from "src/shared/Utils/FormatLogs/FormatLogLabel";
import { FormatLogDate } from "src/shared/Utils/FormatLogs/FormatLogDate";
import { FormatLogUser } from "src/shared/Utils/FormatLogs/FormatLogUser";
import {
  getEntityLabel,
  getActionLabel,
  getActionColor,
} from "src/Pages/TelasSistemas/Utils/LogsTranslate/LogsTranslate";

type Props = {
  open: boolean;
  onClose: () => void;
  data: any;
};

const KeyValueBlock = ({ obj }: { obj: Record<string, any> }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: "divider",
      borderRadius: 3,
      overflow: "hidden",
      backgroundColor: "#fff",
    }}
  >
    {Object.entries(obj).map(([key, value], index, arr) => (
      <Box
        key={key}
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: index !== arr.length - 1 ? "1px solid" : "none",
          borderColor: "divider",
        }}
      >
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 700,
            color: "text.secondary",
            mb: 0.8,
          }}
        >
          {FormatLogLabel(key)}
        </Typography>

        <Box
          component="pre"
          sx={{
            m: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
            fontSize: 16,
            lineHeight: 1.6,
            color: "text.primary",
          }}
        >
          {FormatLogValue(value)}
        </Box>
      </Box>
    ))}
  </Paper>
);

const UpdateTable = ({
  before,
  after,
}: {
  before: Record<string, any>;
  after: Record<string, any>;
}) => {
  const changedKeys = Object.keys({ ...before, ...after }).filter(
    (key) => JSON.stringify(before?.[key]) !== JSON.stringify(after?.[key]),
  );

  if (!changedKeys.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          border: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Typography sx={{ fontSize: 15, color: "text.secondary" }}>
          Nenhuma alteração detectada.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      {changedKeys.map((key, index) => (
        <Box
          key={key}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "180px 1fr 1fr" },
            borderBottom: index !== changedKeys.length - 1 ? "1px solid" : "none",
            borderColor: "divider",
          }}
        >
          <Box sx={{ p: 2.25, bgcolor: "#fafafa" }}>
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 700,
                color: "text.secondary",
                wordBreak: "break-word",
              }}
            >
              {FormatLogLabel(key)}
            </Typography>
          </Box>

          <Box
            sx={{
              p: 2.25,
              bgcolor: "#fff6f6",
              borderLeft: { xs: "none", md: "1px solid" },
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: "error.main",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                mb: 1,
              }}
            >
              Antes
            </Typography>

            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {FormatLogValue(before?.[key])}
            </Box>
          </Box>

          <Box
            sx={{
              p: 2.25,
              bgcolor: "#f4fff6",
              borderLeft: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 800,
                color: "success.main",
                textTransform: "uppercase",
                letterSpacing: 0.6,
                mb: 1,
              }}
            >
              Depois
            </Typography>

            <Box
              component="pre"
              sx={{
                m: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontFamily: "inherit",
                fontSize: 15,
                lineHeight: 1.6,
              }}
            >
              {FormatLogValue(after?.[key])}
            </Box>
          </Box>
        </Box>
      ))}
    </Paper>
  );
};

export const LogsDiffModal = ({ open, onClose, data }: Props) => {
  if (!data) return null;

  const action = String(data.action || "").toUpperCase();
  const before = ParseLogJson(data.data_before);
  const after = ParseLogJson(data.data_after);

  const isCreate = action === "CREATE";
  const isDelete = action === "DELETE";
  const isUpdate = ["UPDATE", "PATCH", "PUT"].includes(action);

  const user = FormatLogUser(data);
  const createdAt = FormatLogDate(data.created_at);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 800, fontSize: 30, mb: 1.5 }}>
            Detalhes do log
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2,
              mb: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 3,
              bgcolor: "#fafafa",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2.5}
              useFlexGap
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={1.2} alignItems="center">
                <PersonIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {user.name}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    {user.description}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1.2} alignItems="center">
                <ScheduleIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                <Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {createdAt.short}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    Data e hora da ação
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Chip
              size="small"
              label={getEntityLabel(data.entity)}
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />

            <Chip
              size="small"
              label={getActionLabel(action)}
              sx={{ fontWeight: 500, backgroundColor: "#EFF8FF", color: "#0288D1"  }}
            />
          </Stack>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{ border: "1px solid", borderColor: "divider" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ p: 3, bgcolor: "#fcfcfd" }}>
        {isCreate && after && typeof after === "object" && (
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
              Registro criado
            </Typography>
            <KeyValueBlock obj={after} />
          </Box>
        )}

        {isDelete && before && typeof before === "object" && (
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
              Registro removido
            </Typography>
            <KeyValueBlock obj={before} />
          </Box>
        )}

        {isUpdate && before && after && (
          <Box>
            <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
              Campos alterados
            </Typography>
            <UpdateTable before={before} after={after} />
          </Box>
        )}

        {!isCreate && !isDelete && !isUpdate && (
          <Stack spacing={2}>
            {before && typeof before === "object" && (
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                  Dados anteriores
                </Typography>
                <KeyValueBlock obj={before} />
              </Box>
            )}

            {after && typeof after === "object" && (
              <Box>
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                  Dados do evento
                </Typography>
                <KeyValueBlock obj={after} />
              </Box>
            )}

            {!before && !after && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  border: "1px dashed",
                  borderColor: "divider",
                  bgcolor: "#fff",
                }}
              >
                <Typography sx={{ fontSize: 15, color: "text.secondary" }}>
                  Este log não possui dados estruturados para exibição.
                </Typography>
              </Paper>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};